// src/components/Admin/ReviewsTab.js
import { useState, useEffect, useCallback } from "react";
import { Star, MessageSquare } from "lucide-react";
import { fetchAllReviews } from "../services/adminReviews";

const RATING_FILTERS = [
  { id: "all", label: "All" },
  { id: "5", label: "5" },
  { id: "4", label: "4" },
  { id: "3", label: "3" },
  { id: "2", label: "2" },
  { id: "1", label: "1" },
];

function Stars({ rating = 0 }) {
  const r = Math.round(Number(rating) || 0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={16}
          className={
            n <= r ? "text-amber-400 fill-amber-400" : "text-gray-300"
          }
        />
      ))}
    </span>
  );
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString();
}

export default function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReviews(await fetchAllReviews());
    } catch (err) {
      console.error("fetchAllReviews error:", err);
      setError("Could not load reviews.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shown = reviews.filter((r) =>
    filter === "all" ? true : Math.round(Number(r.rating) || 0) === Number(filter)
  );

  const avg =
    reviews.length > 0
      ? (
          reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) /
          reviews.length
        ).toFixed(1)
      : "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Reviews</h2>
          <p className="text-gray-500 text-sm">
            {reviews.length} total · {avg} average rating
          </p>
        </div>

        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {RATING_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === f.id
                  ? "bg-[#006C35] text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-gray-500 py-8">Loading reviews...</div>}
      {error && <div className="text-red-600 py-8">{error}</div>}

      {!loading && !error && shown.length === 0 && (
        <div className="flex flex-col items-center justify-center text-gray-400 py-16">
          <MessageSquare size={40} className="mb-3" />
          <p>No reviews here.</p>
        </div>
      )}

      <div className="space-y-4">
        {shown.map((r) => (
          <div
            key={r.id}
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <Stars rating={r.rating} />
              <div className="text-xs text-gray-400">
                {formatDate(r.createdAt)}
              </div>
            </div>

            {r.comment && (
              <p className="mt-2 text-gray-700 whitespace-pre-wrap leading-relaxed">
                {r.comment}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
              <span>Tasker: {r.taskerId || "—"}</span>
              <span>Customer: {r.customerId || "—"}</span>
              <span>Job: {r.jobId || "—"}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}